import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from './query.js';
import { api, type Concept, type StudyBody } from './api.js';
import {
  LifecycleChip,
  ReviewVerdictChip,
  OriginChip,
  StringListEditor,
  ApplicationsEditor,
} from './widgets.js';
import { QuestionEditor } from './QuestionEditor.js';
import { NewQuestionModal } from './NewQuestionModal.js';

/** Editable study-content pane + tagged battle questions + approve/reject. */
export function ConceptEditor({ concept }: { concept: Concept }) {
  const qc = useQueryClient();
  const clusterId = concept.cluster_id;

  // Local editable state, re-seeded whenever the selected concept changes.
  const [coreDefinition, setCoreDefinition] = useState('');
  const [specRequirement, setSpecRequirement] = useState('');
  const [registryTerm, setRegistryTerm] = useState('');
  const [portalLinks, setPortalLinks] = useState<string[]>([]);
  const [body, setBody] = useState<StudyBody>({});
  const [notes, setNotes] = useState('');
  const [showNewQuestion, setShowNewQuestion] = useState(false);

  useEffect(() => {
    setCoreDefinition(concept.core_definition ?? '');
    setSpecRequirement(concept.spec_requirement ?? '');
    setRegistryTerm(concept.registry_term ?? '');
    setPortalLinks(concept.portal_links ?? []);
    setBody(concept.body ?? {});
    setNotes(concept.sme_notes ?? '');
  }, [concept.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // patch4: also bust the single-concept detail cache. Previously only the
  // list cache was invalidated, so navigating away + back showed pre-save
  // values from the in-memory shim cache (until F5 wiped it).
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['concept', clusterId, concept.concept_code] });
    qc.invalidateQueries({ queryKey: ['concepts', clusterId] });
    qc.invalidateQueries({ queryKey: ['clusters'] });
  };

  const patchBody = (patch: Partial<StudyBody>) => setBody((b) => ({ ...b, ...patch }));

  // patch4: include sme_notes so "Save edits" actually persists notes inline
  // (previously the only path that saved sme_notes was Approve/Reject).
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/concept/${concept.id}`, {
        core_definition: coreDefinition,
        spec_requirement: specRequirement,
        registry_term: registryTerm,
        portal_links: portalLinks.map((p) => p.toUpperCase().slice(0, 1)).filter(Boolean),
        body,
        sme_notes: notes,
      }),
    onSuccess: invalidate,
  });

  const review = useMutation({
    mutationFn: (review_status: 'approved' | 'rejected') =>
      api.post('/api/review', { table: 'study', id: concept.id, review_status, sme_notes: notes }),
    onSuccess: invalidate,
  });

  return (
    <div className="detail" data-testid={`concept-detail-${concept.concept_code}`}>
      {/* M9 — full-label chips in the editor pane so SMEs can read the axis
          without referring back to the toolbar. The rail uses the compact
          (`showLabel=false`) variant. */}
      <div className="toolbar">
        <h2 style={{ margin: 0, fontSize: 16 }}>
          <span style={{ fontFamily: 'var(--mono)' }}>{concept.concept_code}</span> {concept.concept_title}
        </h2>
        <OriginChip quality={concept.source_quality} />
        <LifecycleChip status={concept.status} />
        <ReviewVerdictChip status={concept.review_status} />
        {concept.edited_by_sme && <span className="chip edited" data-testid={`badge-edited-${concept.concept_code}`}>edited</span>}
      </div>

      {/* patch4: primary Save edits action lives at the TOP, with a caption that
          clarifies its scope. Previously the only Save edits button sat inside
          the Review card next to SME Notes — SMEs read that as "save my note". */}
      <div className="toolbar" style={{ marginTop: 6, marginBottom: 8 }}>
        <button
          className="primary"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          data-testid="button-save-concept-top"
        >
          {save.isPending ? 'Saving…' : 'Save edits'}
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          Saves Study content, Body, and SME notes.
        </span>
        {save.isError && <span className="err">Save failed</span>}
        {save.isSuccess && <span className="ok">Saved</span>}
      </div>

      {/* ---- Study content ---- */}
      <div className="card">
        <h2>Study content</h2>
        <label>Core definition</label>
        <textarea value={coreDefinition} onChange={(e) => setCoreDefinition(e.target.value)} data-testid="input-core-definition" />

        <label>Spec requirement</label>
        <textarea value={specRequirement} onChange={(e) => setSpecRequirement(e.target.value)} data-testid="input-spec-requirement" />

        <div className="field-grid">
          <div>
            <label>Registry term</label>
            <input value={registryTerm} onChange={(e) => setRegistryTerm(e.target.value)} data-testid="input-registry-term" />
          </div>
          <div>
            <label>Portal links (cluster letters)</label>
            <StringListEditor
              values={portalLinks}
              onChange={setPortalLinks}
              placeholder="A"
              testidPrefix="portal-link"
            />
          </div>
        </div>
      </div>

      {/* ---- Structured body ---- */}
      <div className="card">
        <h2>Body</h2>
        <label>Examiner definition</label>
        <textarea
          value={body.examiner_definition ?? ''}
          onChange={(e) => patchBody({ examiner_definition: e.target.value })}
          data-testid="input-examiner-definition"
        />

        <label>Key distinctions</label>
        <StringListEditor
          values={body.key_distinctions ?? []}
          onChange={(v) => patchBody({ key_distinctions: v })}
          testidPrefix="key-distinction"
        />

        <label>How tested</label>
        <textarea
          value={body.how_tested ?? ''}
          onChange={(e) => patchBody({ how_tested: e.target.value })}
          data-testid="input-how-tested"
        />

        <label>Worked examples</label>
        <StringListEditor
          values={body.worked_examples ?? []}
          onChange={(v) => patchBody({ worked_examples: v })}
          testidPrefix="worked-example"
        />

        <label>Applications</label>
        <ApplicationsEditor
          values={body.applications ?? []}
          onChange={(v) => patchBody({ applications: v })}
        />

        <label>Exam link</label>
        <textarea
          value={body.exam_link ?? ''}
          onChange={(e) => patchBody({ exam_link: e.target.value })}
          data-testid="input-exam-link"
        />
      </div>

      {/* ---- Review verdict ---- */}
      {/* patch4: Save edits moved out of this card to the top toolbar. This card
          now hosts ONLY the verdict actions (Approve / Reject). SME notes still
          lives here because the verdict and the note travel together. */}
      <div className="card">
        <h2>Review</h2>
        <label>SME notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="input-concept-notes" />
        <p className="muted" style={{ fontSize: 12, margin: '4px 0 8px' }}>
          SME notes are persisted by both <strong>Save edits</strong> (above) and <strong>Approve</strong>/<strong>Reject</strong>.
        </p>
        <div className="row-actions" style={{ marginTop: 6 }}>
          {/* Retain a secondary Save edits button here for keyboard-flow users,
              with a distinct testid so the top button is the canonical one. */}
          <button
            className="primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            data-testid="button-save-concept"
          >
            {save.isPending ? 'Saving…' : 'Save edits'}
          </button>
          <button onClick={() => review.mutate('approved')} disabled={review.isPending} data-testid="button-approve-concept">
            Approve
          </button>
          <button className="danger" onClick={() => review.mutate('rejected')} disabled={review.isPending} data-testid="button-reject-concept">
            Reject
          </button>
          {save.isError && <span className="err">Save failed</span>}
          {review.isError && <span className="err">Review failed</span>}
          {(save.isSuccess || review.isSuccess) && <span className="ok">Saved</span>}
        </div>
      </div>

      {/* ---- Intra-cluster questions (1/2/3-mark; portal Qs live in the Portal view) ---- */}
      <div className="card">
        <div className="toolbar" style={{ marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Intra-cluster questions ({concept.templates.length})</h2>
          <span style={{ flex: 1 }} />
          <button
            className="primary sm"
            type="button"
            onClick={() => setShowNewQuestion(true)}
            data-testid="button-suggest-new-question"
          >
            Suggest new question
          </button>
        </div>
        {concept.templates.length === 0 && <p className="muted">No tagged questions for this concept.</p>}
        {concept.templates.map((t) => (
          <QuestionEditor
            key={t.id}
            tmpl={t}
            invalidateKeys={[
              ['concepts', clusterId],
              ['concept', clusterId, concept.concept_code],
              ['clusters'],
            ]}
          />
        ))}
      </div>

      {showNewQuestion && (
        <NewQuestionModal
          clusterId={clusterId}
          conceptCode={concept.concept_code}
          onClose={() => setShowNewQuestion(false)}
          onSaved={invalidate}
        />
      )}
    </div>
  );
}
